import UpcomingLive from '@/components/ui/PDP/ECommBox/UpcomingLive';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { getLiveVariants } from '@/hooks/PDP/useProgramCatalogRelations';
import { getLiveType, isLive } from '@/hooks/PDP/useProgramVariantSelection';
import {
  getAddressFromVenue,
  getExpiredStatus,
  getPriceFromContent,
  isWaitlist
} from '../PDP/helpers';
import useEcommPanelData, { EcommPanelParams } from './hooks/useEcommData';
import { useGetUpcomingRegistrationsQuery } from '@/redux/api/client/registration';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAuth } from 'react-oidc-context';
import { transformIfPLIUrl } from '@/utils/helpers';
import { AnalyticsContext } from '@/analytics/constants';
import useCatalogLabels from '@/hooks/PDP/useCatalogLabels';
import Loading from '../PDP/ECommBox/UpcomingLive/components/Loading';
import { usePDPSliceSelectorContext } from '@/hooks/shared/usePDPSliceSelector';
import {
  resetPDPContext,
  setCatalogRelations,
  setHardSelectedVariant,
  setMaterialsLoading,
  setSoftSelectedVariant
} from '@/redux/slices/pdpSlice';
import { ItemListAnalyticsModel } from '@/@types/client-api';

type EcommPanelLiveProgramProps = {
  params: EcommPanelParams;
  onClose: () => void;
  context?: AnalyticsContext;
  origin?: 'default' | 'credit-plan';
  setIsLoading?: (loading?: boolean) => void;
  itemListAnalyticsModel?: ItemListAnalyticsModel;
};

const EcommPanelLiveProgram = ({
  params,
  onClose,
  context,
  origin,
  setIsLoading,
  itemListAnalyticsModel
}: EcommPanelLiveProgramProps) => {
  const { sliceName } = usePDPSliceSelectorContext();

  const dispatch = useDispatch();
  const auth = useAuth();

  const { data, isLoading, isError: isEcommPanelDataError } = useEcommPanelData(params);
  const {
    data: registrations,
    isFetching: isRegFetching,
    isLoading: isRegLoading,
    isError: isUpcomingRegistrationError
  } = useGetUpcomingRegistrationsQuery(!auth.isAuthenticated ? skipToken : undefined);

  useEffect(() => {
    if (!!isEcommPanelDataError || !!isUpcomingRegistrationError) throw Error;
  }, [isEcommPanelDataError, isUpcomingRegistrationError]);

  const { liveEcomLabels, isLoading: isLabelsLoading } = useCatalogLabels();

  const loading = useMemo(
    () => isLoading || isRegFetching || isRegLoading || isLabelsLoading,
    [isLabelsLoading, isLoading, isRegFetching, isRegLoading]
  );

  useEffect(() => {
    setIsLoading && setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  const liveVariants = useMemo(() => getLiveVariants(data), [data]);

  const variants = useMemo(
    () => ({
      variants: liveVariants.map(variant => {
        const priceInfo = getPriceFromContent(variant.content);
        const registrationInfo = registrations?.find(r => r.pk === variant.content.code);
        return {
          code: variant.content.code!,
          location: variant.content.location ?? '',
          eventStartDate: variant.content.eventStartDate ?? '',
          eventEndDate: variant.content.eventEndDate ?? '',
          timeZoneIdentifier: variant.content.timeZoneIdentifier ?? '',
          type: getLiveType(variant),
          displayName: variant.content.displayName ?? '',
          itemClass: variant.content.itemClassId,
          retailPrice: priceInfo.rawRetailPrice,
          discountPrice: priceInfo.rawDiscountPrice,
          price: priceInfo.price,
          originalPrice: priceInfo.strikePrice,
          retail: priceInfo.retail,
          isExpired: getExpiredStatus(variant.content),
          registered: registrations?.some(reg => reg.pk === variant.content.code),
          sk: variant.content.externalSystemKey,
          ecomBoxSessions: variant.content.ecomBoxSessions,
          customDuration: variant.content.customDuration,
          isLive: isLive(variant),
          waitlist: isWaitlist(variant.content),
          ...getAddressFromVenue(variant.content.typedVenue),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          launchUrl: registrationInfo ? (variant as any)?.launchUrl : undefined
        };
      })
    }),
    [liveVariants, registrations]
  );

  useEffect(() => {
    dispatch(resetPDPContext(sliceName));
    return () => {
      dispatch(resetPDPContext(sliceName));
    };
  }, [dispatch, sliceName]);

  useEffect(() => {
    if (!data) {
      return;
    }
    const selected = variants.variants.find(variant => variant.code === params.pk)!;
    if (variants.variants.length === 1)
      dispatch(setHardSelectedVariant({ sliceName, variant: selected }));
    else dispatch(setSoftSelectedVariant({ sliceName, variant: selected }));
    dispatch(setCatalogRelations({ sliceName, catalogRelations: variants }));
    dispatch(setMaterialsLoading({ sliceName, isMaterialsLoading: false }));
  }, [variants, dispatch, params, data, sliceName]);

  if (loading) {
    return <Loading />;
  }

  if (!data) {
    return null;
  }

  return (
    <UpcomingLive
      labels={liveEcomLabels}
      onClosePanel={onClose}
      title={params.title}
      seeDetailsLink={transformIfPLIUrl(params.url)}
      autoOpenChangeOrCancelRegistrationPanel={params.action === 'ChangeOrCancelRegistration'}
      context={context}
      origin={origin}
      creditTrackerCode={params.creditTrackerCode}
      itemListAnalyticsModel={itemListAnalyticsModel}
    />
  );
};

export default EcommPanelLiveProgram;
